import { z } from 'zod';

/**
 * A person's or company's name: one line, no control characters (they end up
 * in email subjects). Also refuses Unicode line/paragraph separators and bidi
 * overrides (which can disguise a name); ZWJ/ZWNJ stay, real names use them.
 */
export const displayNameSchema = z
  .string()
  .trim()
  .min(2, 'Enter at least 2 characters')
  .max(120, 'Keep it to 120 characters or fewer')
  .regex(
    /^[^\p{Cc}\p{Zl}\p{Zp}\u202A-\u202E\u2066-\u2069]*$/u,
    'Must not contain line breaks or control characters',
  );

// Rule 6: strip emails and phone numbers from any stored free text.
// The lookbehind starts a match only at the beginning of a local-part run,
// so a long `aaaa@aaaa` with no dot can't backtrack quadratically.
const EMAIL_RE = /(?<![A-Z0-9._%+-])[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
// Digit groups joined by single separators (space . -), optionally
// parenthesised, optional leading +. `phoneLike` then decides which of
// these are phone numbers rather than salaries, dates or years.
const NUMBER_RUN_RE = /(?<![\w/+])\+?\(?\d+\)?(?:[\s.-]\(?\d+\)?)*(?![\w/])/g;

function phoneLike(m: string): boolean {
  const digits = m.replace(/\D/g, '').length;
  if (digits < 7 || digits > 15) return false;
  // International format is unambiguous: +44 7911 123456, +15551234567.
  if (m.startsWith('+')) return true;
  if (/^\d{1,4}[-./]\d{1,2}[-./]\d{1,4}$/.test(m)) return false; // dates
  const groups = m.match(/\d+/g)!;
  if (groups.every((g) => /^(19|20)\d\d$/.test(g))) return false; // runs of years
  // Trunk prefix or area code in parentheses: 07911 123456, (555) 123-4567.
  if (/^(\(|0\d)/.test(m) && groups.length >= 2) return true;
  // Otherwise only the classic grouped shape (555-0100-2222, 20 7946 0958),
  // so salary ranges and ids (90000-120000, 1200000) are left alone.
  return groups.length >= 3 && groups.every((g) => g.length <= 4);
}

export function stripContactInfo(text: string): string {
  return text
    .replace(EMAIL_RE, '[email removed]')
    .replace(NUMBER_RUN_RE, (m) => (phoneLike(m) ? '[phone removed]' : m))
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** Role free text (title + summary) with contact details stripped — every role write path. */
export function stripRoleContactInfo<T extends { title: string; summary: string | null }>(
  input: T,
): T {
  return {
    ...input,
    title: stripContactInfo(input.title),
    summary: input.summary === null ? null : stripContactInfo(input.summary),
  };
}

/** `stripContactInfo` per line, so multi-line notes keep their line breaks. */
export function stripContactInfoLines(text: string): string {
  return text.split(/\r?\n/).map(stripContactInfo).join('\n').trim();
}
